'use client';

import { cn } from '@heidi/utils';
import * as React from 'react';
import { useDesignSystemIntl } from '../../../hooks/use-design-system-intl';

import { panelFloatingModeClasses, panelPinnedModeClasses } from './panel.styles';
import { PANEL_WIDTH, type PanelProps } from './panel.types';

/**
 * Pure styling wrapper for secondary panel content.
 * Provides consistent visual appearance for panels without any state management.
 *
 * Use this as a building block for custom panel implementations that need
 * specific state management or context (like SidebarPanel).
 *
 * @example
 * ```tsx
 * // Pinned - in document flow
 * <Panel isPinned state="open">
 *   <PanelContent />
 * </Panel>
 *
 * // Floating - fixed positioning
 * <Panel isPinned={false} state="open" style={{ left: '4rem' }}>
 *   <PanelContent />
 * </Panel>
 * ```
 */
const Panel = React.forwardRef<HTMLDivElement, PanelProps>(
  (
    {
      'data-testid': testId,
      id,
      className,
      children,
      isInset = false,
      isPinned = true,
      width = PANEL_WIDTH,
      state = 'open',
      switching,
      style,
      ...props
    },
    ref
  ) => {
    const isFloating = !isPinned;
    const panelClasses = isFloating ? panelFloatingModeClasses : panelPinnedModeClasses;
    const intl = useDesignSystemIntl();
    const panelLabel = intl.formatMessage({
      defaultMessage: 'Secondary navigation panel',
      id: 'XzPQCp6EUU',
      description: 'Accessible label for the page shell secondary navigation panel',
    });

    return (
      <div
        ref={ref}
        id={id}
        tabIndex={id ? -1 : undefined}
        data-testid={testId}
        data-slot="panel"
        data-state={state}
        data-switching={switching ? 'true' : undefined}
        role="complementary"
        aria-label={panelLabel}
        aria-hidden={state === 'closed'}
        className={cn(panelClasses, isInset && 'py-1', className)}
        style={{
          width: typeof width === 'number' ? `${width}px` : width,
          ...style,
        }}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore - inert is a valid HTML attribute but not in React types yet
        // Note: inert is only applied to floating panels because:
        // - Floating panels stay in DOM when closed (hidden overlay) and need focus prevention
        // - Pinned panels don't render in closed state (SidebarPanel returns null when no content)
        inert={isFloating && state === 'closed' ? '' : undefined}
        {...props}
      >
        {children}
      </div>
    );
  }
);
Panel.displayName = 'Panel';

export { Panel };
